import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GIS_LAYER_SEARCH } from '@rsos/capstone/src/constants/capabilities';
import useHasCapability from '@rsos/hooks/useHasCapability';
import ActionCommandExperience, {
  type ActionExperienceHandle,
} from './ActionCommandExperience';
import styles from './GlobalMapSearch.module.css';
import {
  ClearButton,
  Input,
  SearchBar,
  SearchIcon,
  Wrapper,
} from './GlobalMapSearch.styles';
import RegularSearchExperience, {
  type RegularExperienceHandle,
} from './RegularSearchExperience';
import SlashCommandExperience, {
  type SlashExperienceHandle,
} from './SlashCommandExperience';
import {
  trackGlobalMapSearchClear,
  trackGlobalMapSearchFocus,
  trackGlobalMapSearchModeChange,
  trackGlobalMapSearchView,
  type GlobalMapSearchMode,
} from './globalMapSearchTrackings';

/**
 * Thin shell that owns the input, clear button, and focus
 * lifecycle, then routes typing to one of three always-mounted
 * sibling experiences based on the input prefix:
 *
 *   - `//` → action experience (suggestion-only `//` actions, e.g.
 *            `// add <external gis layer>`).
 *   - `/`  → slash command experience (existing `/call`,
 *            `/transcript`, `/location`, etc.).
 *   - else → regular search experience.
 *
 * Mode detection MUST check `//` before `/` because `'//foo'.startsWith('/')`
 * is also true. Keeping all three experiences mounted preserves each
 * workflow's state across a round-trip through the others.
 */
const GlobalMapSearch: React.FC = () => {
  const { hasCapability } = useHasCapability(GIS_LAYER_SEARCH);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isFocusedRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  // Bubbled up from the prefix-aware experiences: the suffix that, when
  // appended to `searchTerm`, equals what Tab will autofill. Rendered
  // as inline ghost text directly inside the search field. Each
  // experience owns its own slot so a hidden experience can't clobber
  // a visible one's ghost when both update on the same render (JSX
  // render order would otherwise let the later sibling overwrite the
  // earlier sibling's value).
  const [slashGhostSuffix, setSlashGhostSuffix] = useState('');
  const [actionGhostSuffix, setActionGhostSuffix] = useState('');

  const regularRef = useRef<RegularExperienceHandle>(null);
  const slashRef = useRef<SlashExperienceHandle>(null);
  const actionRef = useRef<ActionExperienceHandle>(null);
  const hasTrackedViewRef = useRef(false);
  const lastTrackedModeRef = useRef<GlobalMapSearchMode | null>(null);

  // `//` MUST be checked before `/` since the bare `startsWith('/')`
  // is also true for double-slash inputs. `///` is what3words — never
  // action or slash-command mode even though it shares the `/` prefix.
  const isActionInput =
    searchTerm.startsWith('//') && !searchTerm.startsWith('///');
  const inputMode: GlobalMapSearchMode = isActionInput
    ? 'action'
    : searchTerm.startsWith('/') && !searchTerm.startsWith('///')
      ? 'command'
      : 'search';

  // Render only the visible experience's ghost; hidden experiences
  // still update their own slots, but the host chooses what paints.
  const ghostSuffix =
    inputMode === 'action'
      ? actionGhostSuffix
      : inputMode === 'command'
        ? slashGhostSuffix
        : '';

  // The nav slot mounts independently, so it may not exist yet when we do.
  // Watch the DOM until it appears rather than checking only once.
  useEffect(() => {
    const slotId = 'navbar-center-slot';
    const existing = document.getElementById(slotId);
    if (existing) {
      setPortalTarget(existing);
      return;
    }
    const observer = new MutationObserver(() => {
      const el = document.getElementById(slotId);
      if (el) {
        setPortalTarget(el);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        regularRef.current?.onClose();
        setIsFocused(false);
        isFocusedRef.current = false;
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fire once when the bar is actually painted into the nav slot.
  useEffect(() => {
    if (!hasCapability || !portalTarget || hasTrackedViewRef.current) {
      return;
    }
    hasTrackedViewRef.current = true;
    trackGlobalMapSearchView();
  }, [hasCapability, portalTarget]);

  // Skip the initial `search` mode so we only report real prefix switches.
  useEffect(() => {
    if (!hasCapability || !portalTarget) {
      return;
    }
    if (lastTrackedModeRef.current === null) {
      lastTrackedModeRef.current = inputMode;
      return;
    }
    if (lastTrackedModeRef.current === inputMode) {
      return;
    }
    lastTrackedModeRef.current = inputMode;
    trackGlobalMapSearchModeChange({ mode: inputMode });
  }, [hasCapability, portalTarget, inputMode]);

  // `preserveInput` is set by scoped-result selections (`/transcript`,
  // `/chat`, `/parcel` rows) so picking a hit doesn't wipe the user's
  // query — matching the regular result-click path. Action commands and
  // arg picks still clear since their input was a one-shot directive.
  const handleSlashComplete = useCallback(
    (opts?: { preserveInput?: boolean }) => {
      if (!opts?.preserveInput) setSearchTerm('');
      regularRef.current?.onClose();
      isFocusedRef.current = false;
      inputRef.current?.blur();
    },
    [],
  );

  const handleClear = useCallback(() => {
    trackGlobalMapSearchClear();
    setSearchTerm('');
    regularRef.current?.onClear();
    slashRef.current?.onClear();
    actionRef.current?.onClear();
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setSearchTerm(next);
    // Regular experience owns the wipe (clear results, abort in-flight,
    // drop pinned popover). No-ops on slash inputs.
    regularRef.current?.onChange(next);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const ref =
        inputMode === 'action'
          ? actionRef
          : inputMode === 'command'
            ? slashRef
            : regularRef;
      try {
        ref.current?.onKeyDown(e);
      } catch {
        regularRef.current?.onClose();
        setIsFocused(false);
        isFocusedRef.current = false;
      }
    },
    [inputMode],
  );

  if (!hasCapability || !portalTarget) return null;

  return createPortal(
    <Wrapper ref={wrapperRef}>
      <div className={styles.searchBarSlot}>
        <SearchBar>
          <SearchIcon
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </SearchIcon>
          <div className={styles.inputWrap}>
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search (calls, places, responders, layers...)"
              value={searchTerm}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                trackGlobalMapSearchFocus();
                isFocusedRef.current = true;
                setIsFocused(true);
                regularRef.current?.onFocus();
                slashRef.current?.onFocus();
                actionRef.current?.onFocus();
              }}
              onBlur={() => {
                isFocusedRef.current = false;
                setIsFocused(false);
              }}
            />
            {ghostSuffix && (
              <div className={styles.ghost} aria-hidden>
                <span className={styles.ghostInvisible}>{searchTerm}</span>
                <span>{ghostSuffix}</span>
              </div>
            )}
          </div>
          {searchTerm && (
            <ClearButton onClick={handleClear} aria-label="Clear search">
              ×
            </ClearButton>
          )}
        </SearchBar>

        <RegularSearchExperience
          ref={regularRef}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isHidden={inputMode !== 'search'}
          isFocused={isFocused}
          isFocusedRef={isFocusedRef}
          inputRef={inputRef}
        />
        <SlashCommandExperience
          ref={slashRef}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isHidden={inputMode !== 'command' || !isFocused}
          inputRef={inputRef}
          isFocusedRef={isFocusedRef}
          onComplete={handleSlashComplete}
          onGhostSuffixChange={setSlashGhostSuffix}
        />
        <ActionCommandExperience
          ref={actionRef}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isHidden={inputMode !== 'action' || !isFocused}
          inputRef={inputRef}
          isFocusedRef={isFocusedRef}
          onGhostSuffixChange={setActionGhostSuffix}
        />
      </div>
    </Wrapper>,
    portalTarget,
  );
};

export default GlobalMapSearch;
